import * as R from "ramda";
import { at } from "lodash/fp";
import { omit } from "underscore";
import { format, isValid, parse, parseISO } from "date-fns";
import { DAYS, MONTHS } from "../../../constants";
import { find, orderBy, trim, uniqBy } from "lodash";
import { EN_DASH } from "@digitallab/grid-common-components";

export const emailToName = (email) => (typeof email === "string" ? email.split("@")[0]?.replace(/\./g, " ") : "");

export const sanitizeStringNullValue = (value, valueToReturn = '') => {
  if (value === "null") {
    return valueToReturn;
  }

  return value;
}

export const getText = (text) => R.compose(R.toLower, R.trim)(text);

export const valueOrEmpty = (value, itsADate = false, defaultValue = "", dateFormat = "dd-MMM-yyyy") => {
  if (value && itsADate) {
    return format(new Date(value), dateFormat);
  } else if (value && value !== "null" && !itsADate) {
    return value;
  } else {
    return defaultValue;
  }
};

export const stringOrEmpty = (value, { propLabel = "value", defaultValue = "", joinString = ", " }) => {
  if (Array.isArray(value) && value.length > 0) {
    if (typeof value[0] === "object") return value.map((item) => item[propLabel]).join(joinString);
    else return value.join(joinString);
  }
  return defaultValue;
};

export const filterByQuery = (data, query, propFilter = ["name"], propSort = ["name"], reverse = false) => {
  if (!query) {
    R.sort(R.descend(R.path(propSort)))(data);
  }
  const _query = getText(query);
  const valuesAt = at(propFilter);
  const result = R.compose(
    R.sort(R.descend(R.path(propSort))),
    R.filter(R.compose(R.contains(_query), getText, R.compose(R.join(""), valuesAt)))
  )(data);

  return reverse ? result.reverse() : result;
};

export const omitOrReturn = (obj) => {
  if (obj && typeof obj === "object" && Object.keys(obj).length > 0) {
    return omit(obj, "__typename");
  }
  return obj;
};

export const omitOrReturnArray = (obj) => {
  if (obj && Array.isArray(obj)) return obj.map((item) => omit(item, "__typename"));

  return obj;
};

export const getLocationText = (itemWithLocationDetails) => {
  let { buildingLocation, floorAndRoomLocation } = itemWithLocationDetails ?? {};
  if (!buildingLocation && !floorAndRoomLocation) {
    return "";
  }
  if (typeof buildingLocation === "object") {
    buildingLocation = buildingLocation.value;
  }
  if (typeof floorAndRoomLocation === "object") {
    floorAndRoomLocation = floorAndRoomLocation.value;
  }
  if (!buildingLocation && !floorAndRoomLocation) {
    return "";
  }
  return `${buildingLocation ?? ""};${floorAndRoomLocation ?? ""}`;
};

export const parseTime = (time, { timeFormat = "h:mm aaa", parseFormat = "HH:mm:ss" } = {}) => {
  if (!time) {
    return "";
  }

  try {
    const parsedDate = parse(time, parseFormat, new Date());
    return format(parsedDate, timeFormat);
  } catch (e) {
    return time;
  }
};

export const parseTimeLocal = (time) => {
  if (!time) {
    return "";
  }
  try {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let runStartDateNow = new Date();
    runStartDateNow.setHours(0, 0, 0, 0);
    const runStartTimesplitTime = time.split(":");
    const runStartTimeseconds =
      +runStartTimesplitTime[0] * 60 * 60 + +runStartTimesplitTime[1] * 60 + +runStartTimesplitTime[2];
    runStartDateNow.setSeconds(runStartTimeseconds);
    const parsedDateLocal = runStartDateNow.toLocaleString("en-GB", {
      timeZone: localTimeZone
    });
    const runStartTimesplitTimeNew = parsedDateLocal.split(",");
    const formattedVal = runStartTimesplitTimeNew[0].split("/");
    runStartDateNow = new Date(formattedVal[2], formattedVal[1], formattedVal[0]);
    runStartDateNow.setHours(0, 0, 0, 0);
    const runStartTimesplitTimeNewVal = runStartTimesplitTimeNew[1].split(":");
    const runStartTimesecondsnew =
      +runStartTimesplitTimeNewVal[0] * 60 * 60 +
      +runStartTimesplitTimeNewVal[1] * 60 +
      +runStartTimesplitTimeNewVal[2];
    runStartDateNow.setSeconds(runStartTimesecondsnew);
    let hours = runStartDateNow.getHours();
    let minutes = runStartDateNow.getMinutes();
    let ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    hours = hours <= 9 ? "0" + hours : hours;
    minutes = minutes < 10 ? "0" + minutes : minutes;
    return `${hours}:${minutes} ${ampm}`;
  } catch (e) {
    return time;
  }
};
export const changeDateFormat = (inputDate, format = null) => {
  try {
    if (typeof inputDate === "string") {
      if (format === null) {
        const dateObj = new Date(inputDate).toUTCString();
        const splitDateFormat = dateObj?.split(" ");
        const currentDate = splitDateFormat[1] + "-" + splitDateFormat[2] + "-" + splitDateFormat[3];
        return new Date(currentDate);
      }
      if (format === "dd-MMM-yyyy" || format === "DD-MMM-YYYY") {
        const dateObj = new Date(inputDate).toUTCString();
        const splitDateFormat = dateObj?.split(" ");
        return `${splitDateFormat[1]}-${splitDateFormat[2]}-${splitDateFormat[3]}`;
      }
      if (format === "mm/dd/yyyy h:mm:ss") {
        const dateObjString = new Date(inputDate).toUTCString();
        let dateArray = inputDate.split("T");
        dateArray = dateArray[0].split("-");
        const splitDateFormat = dateObjString?.split(" ");
        const currentDate = parseInt(dateArray[1]) + "/" + dateArray[2] + "/" + splitDateFormat[3];
        const timeVal = inputDate.split("T");
        const time = timeVal[1].split(":");
        let hours = parseInt(time[0]);
        const seconds = time[2].slice(0, 2);
        let mid = " AM";
        if (hours === 12) {
          mid = " PM";
        }
        if (hours > 12) {
          hours = hours % 12;
          mid = " PM";
          if (hours === 0) {
            mid = " AM";
          }
        }
        if (hours < 10) {
          hours = "0" + hours;
        }
        return currentDate + ", " + hours + ":" + time[1] + ":" + seconds + mid;
      }
      if (format === "h:mm") {
        let timeVal = inputDate.split("T");
        let time = timeVal[1].split(":");
        let hours = parseInt(time[0]);

        let mid = " am";
        if (hours === 12) {
          mid = " pm";
        }
        if (hours > 12) {
          hours = hours % 12;
          mid = " pm";
          if (hours === 0) {
            mid = " am";
          }
        }
        return hours + ":" + time[1] + mid;
      }
      if (format === "ISOToDate") {
        const timeVal = inputDate.split("T");
        const date = timeVal[0].split("-");
        const month = parseInt(date[1]) - 1;
        const time = timeVal[1].split(":");
        const hours = parseInt(time[0]);
        const dateObj = new Date(date[2] + "-" + MONTHS[month] + "-" + date[0]);
        dateObj.setTime(dateObj.getTime() + hours * 60 * 60 * 1000);
        return dateObj;
      }
    } else {
      if (format === "EEEE, dd-MMM-yyyy") {
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const weekDay = inputDate.getDay();
        const monthVal = inputDate.getMonth();
        const convertedDate = inputDate.toLocaleString("en-GB", {
          timeZone: localTimeZone
        });

        const splitDate = convertedDate?.split(",");
        if (splitDate.count === 0) {
          throw new Error("No valid date found for conversion");
        }

        const splitDateFormat = splitDate[0]?.split("/");
        if (splitDate.count === 0) {
          throw new Error("No valid date found for conversion");
        }

        let day = splitDateFormat[0];
        let year = splitDateFormat[2];

        return DAYS[weekDay] + ", " + day + "-" + MONTHS[monthVal] + "-" + year + " .";
      }
      if (format === "DD-MMM-YYYY" || format === "dd-MMM-yyyy") {
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const monthVal = inputDate.getMonth();
        const convertedDate = inputDate.toLocaleString("en-GB", {
          timeZone: localTimeZone
        });

        const splitDate = convertedDate?.split(",");
        if (splitDate.count === 0) {
          throw new Error("No valid date found for conversion");
        }

        const splitDateFormat = splitDate[0]?.split("/");
        if (splitDate.count === 0) {
          throw new Error("No valid date found for conversion");
        }

        let day = splitDateFormat[0];
        let year = splitDateFormat[2];

        return day + "-" + MONTHS[monthVal] + "-" + year;
      }
      if (format === "yyyy-MM-dd") {
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const convertedDate = inputDate.toLocaleString("en-GB", {
          timeZone: localTimeZone
        });

        const splitDate = convertedDate?.split(",");
        if (splitDate.count === 0) {
          throw new Error("No valid date found for conversion");
        }

        const splitDateFormat = splitDate[0]?.split("/");
        if (splitDate.count === 0) {
          throw new Error("No valid date found for conversion");
        }

        let day = splitDateFormat[0];
        var month = splitDateFormat[1];
        let year = splitDateFormat[2];

        return year + "-" + month + "-" + day;
      }
      if (format === "ISO") {
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const convertedDate = inputDate.toLocaleString("en-GB", {
          timeZone: localTimeZone
        });

        let isoVal = inputDate.toTimeString();
        isoVal = isoVal.split(" ");
        const splitDate = convertedDate?.split(",");
        if (splitDate.count === 0) {
          throw new Error("No valid date found for conversion");
        }

        const splitDateFormat = splitDate[0]?.split("/");
        if (splitDate.count === 0) {
          throw new Error("No valid date found for conversion");
        }

        let day = splitDateFormat[0];
        let month = splitDateFormat[1];
        let year = splitDateFormat[2];

        return `${year}-${month}-${day}T${isoVal[0]}.000Z`;
      }
    }
  } catch (error) {
    console.log(error);
  }
};
export const getTimeZoneOffset = (dateObj = null) => {
  if (dateObj === null) {
    return null;
  }
  if (!dateObj instanceof Date) {
    dateObj = new Date();
  }
  var t = dateObj.toString().match(/[-+]\d{4}/)[0];
  return t.substring(0, 3) + ":" + t.substring(3);
};
const envs = ["dev", "test", "staging", "stage", "hotfix"];

export const getEnv = () => {
  if (envs?.includes(process.env.REACT_APP_ENV)) {
    if (process.env.REACT_APP_ENV === "staging" || process.env.REACT_APP_ENV === "stage") {
      return "stg";
    } else {
      return process.env.REACT_APP_ENV;
    }
  } else {
    return null;
  }
};

export const getEnvLebel = (currentEnv) => {
  switch (currentEnv) {
    case "hotfix":
      return "Test Hotfix Environment";
    case "test":
      return "Test Environment";
    case "stg":
      return "Staging Environment";
    case "dev":
      return "Development Environment";
    default:
      return "";
  }
};

export const checkEnvReturnValue = (currentEnv, returnVal) => {
  if (currentEnv === "hotfix" || currentEnv === "test" || currentEnv === "stg" || currentEnv === "dev") {
    return returnVal[0];
  } else {
    return returnVal[1];
  }
};

export const getHashCommitValue = (url) => {
  const fragments = url.split("/");
  return fragments[fragments.length - 1].toUpperCase();
};

export const scrollAvailability = () => {
  const currLocation = window.location.href.split("/");
  return currLocation[currLocation.length - 1] === "info" ? "auto" : "hidden";
};

export const replaceEmptyWithHyphen = (val) => {
  return val === "" || val === "null" || val === null || val === undefined ? EN_DASH : val;
};

export function rearangeDisplayDate(inputDate) {
  // expects Y-m-d
  var splitDate = inputDate.split("-");
  if (splitDate.count === 0) {
    return "-";
  }
  var year = splitDate[0];
  var month = splitDate[1];
  var day = splitDate[2];

  return day + "-" + month + "-" + year;
}
export const sortedList = (items, fieldName = [], sortDirection = ["asc"]) => {
  return orderBy(items, [...fieldName], [...sortDirection]);
};

export const uniqList = (items, fieldName) => {
  return uniqBy(items, fieldName);
};

export const getSortedUniqueList = (list = [], columnName) => {
  return uniqList(list.map((x) => trim(x[columnName]))).sort();
};

export const getUniqValuesWithCaseInsensitive = (values) => {
  let uniqueValues = [];
  let repeatedValuesCaseInSensitive = [];
  values?.forEach((value) => {
    if (!repeatedValuesCaseInSensitive?.includes(value?.toLowerCase())) {
      uniqueValues.push(value);
      repeatedValuesCaseInSensitive.push(value?.toLowerCase());
    }
  });
  return uniqueValues;
};

export const convertToSnakeCase = (value) => {
  return value
    .replace(/([A-Z])/g, "_$1")
    .trim()
    ?.toLowerCase();
};

export const convertToCamelCase = (str) => {
  if (!str?.includes("_")) {
    return str;
  }
  let arr = str.split("_");

  return arr
    .map((value, index) => {
      return index === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);
    })
    ?.join("");
};
export const convertStrToObjColumns = (savedColumn) => {
  let displayColumn = typeof savedColumn === "string" ? JSON.parse(savedColumn) : savedColumn;
  displayColumn = {
    ...displayColumn,
    displayDefinition:
      typeof displayColumn?.displayDefinition === "string"
        ? JSON.parse(displayColumn?.displayDefinition)
        : displayColumn?.displayDefinition
  };

  return displayColumn;
};
export const stringComparator = (valueA, valueB) => {
  if (valueA === valueB) return 0;
  return valueA > valueB ? 1 : -1;
};

export const validURL = (str) => {
  try {
    new URL(str);
    return true;
  } catch (_) {
    return false;
  }
};

export const validateSelection = (baseObj, findingData) => {
  const valData = find(baseObj, findingData);
  if (valData) return valData?.value || null;
};

export const sortLogs = (data) => {
  return data?.sort((a, b) => {
    const compareWithEmpty = (dataA, dataB) => {
      if (dataA === dataB) return 0;
      if (!dataA) return -1;
      if (!dataB) return 1;
      return dataB.localeCompare(dataA);
    };

    const startDateComparison = compareWithEmpty(a.runStartDate || a.actionDate, b.runStartDate || b.actionDate);
    if (startDateComparison !== 0) return startDateComparison;

    const startTimeComparison = compareWithEmpty(a.runStartTime || a.actionTime, b.runStartTime || b.actionTime);
    if (startTimeComparison !== 0) return startTimeComparison;

    const endDateComparison = compareWithEmpty(a.runEndDate, b.runEndDate);
    if (endDateComparison !== 0) return endDateComparison;

    const endTimeComparison = compareWithEmpty(a.runEndTime, b.runEndTime);
    if (endTimeComparison !== 0) return endTimeComparison;

    return compareWithEmpty(a.createdAt, b.createdAt);
  });
};

export const prepareForComparison = (obj) => {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(prepareForComparison);
  }

  const newObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];

      if (value === "null") {
        newObj[key] = "";
      } else if (value === null || value === undefined) {
        newObj[key] = "";
      } else if (typeof value === "object") {
        newObj[key] = prepareForComparison(value);
      } else {
        newObj[key] = value;
      }
    }
  }

  return newObj;
};

/**
 * Converts a UTC datetime string to user's local timezone for display
 * @param {string} utcDateTimeString - UTC ISO 8601 string (e.g., "2024-01-26T20:30:00.000Z")
 * @param {object} options - Formatting options
 * @param {string} options.variant - "datetime" | "date" | "time" (default: "datetime")
 * @param {number} options.timeFormat - 12 or 24 hour format (default: 24)
 * @returns {string} Formatted datetime in user's local timezone
 */
export const formatUtcToLocal = (utcDateTimeString, options = {}) => {
  const { variant = "datetime", timeFormat = 12 } = options;

  if (!utcDateTimeString) return "-";

  const date = parseISO(utcDateTimeString);

  if (!isValid(date)) return "-";

  const datePattern = "dd-MMM-yyyy";
  const timePattern = timeFormat === 12 ? "h:mm a" : "HH:mm";

  switch (variant) {
    case "date":
      return format(date, datePattern);
    case "time":
      return format(date, timePattern);
    case "datetime":
    default:
      return format(date, `${datePattern} ${timePattern}`);
  }
};

/**
 * Gets the date-only display value for datetime fields.
 * Uses UTC datetime with fallback to legacy date field.
 * @param {object} data - Row data object
 * @param {string} utcDateTimeField - Field name for UTC datetime (e.g., 'runStartDateTime')
 * @param {string} legacyDateField - Field name for legacy date (e.g., 'runStartDate')
 * @returns {string} Formatted date string (DD-MMM-YYYY) or "-"
 */
export const getDateTimeDisplayValue = (data, utcDateTimeField, legacyDateField) => {
  const utcDateTime = data?.[utcDateTimeField];
  if (utcDateTime) {
    return formatUtcToLocal(utcDateTime, { variant: "date" });
  }
  const legacyDate = data?.[legacyDateField];
  if (legacyDate) {
    return format(parseISO(legacyDate), "dd-MMM-yyyy");
  }
  return "-";
};

/**
 * Creates an ag-grid valueFormatter that uses UTC datetime with fallback to legacy date.
 * Returns date-only format for display in cells (time shown via tooltip).
 * @param {string} utcDateTimeField - Field name for UTC datetime (e.g., 'runStartDateTime')
 * @param {string} legacyDateField - Field name for legacy date (e.g., 'runStartDate')
 * @returns {function} ag-grid valueFormatter function
 */
export const createDateTimeValueFormatter = (utcDateTimeField, legacyDateField) => (params) => {
  return getDateTimeDisplayValue(params?.data, utcDateTimeField, legacyDateField);
};

/**
 * Gets the full datetime string for tooltip display.
 * Uses UTC datetime with fallback to legacy date/time fields.
 * @param {object} data - Row data object
 * @param {string} utcDateTimeField - Field name for UTC datetime
 * @param {string} legacyDateField - Field name for legacy date
 * @param {string} legacyTimeField - Field name for legacy time
 * @returns {string} Formatted datetime string
 */
export const getDateTimeTooltipValue = (data, utcDateTimeField, legacyDateField, legacyTimeField) => {
  const utcDateTime = data?.[utcDateTimeField];
  if (utcDateTime) {
    return formatUtcToLocal(utcDateTime, { variant: "datetime" });
  }
  const legacyDate = data?.[legacyDateField];
  const legacyTime = data?.[legacyTimeField];
  if (legacyDate && legacyTime) {
    const formattedDate = format(parseISO(legacyDate), "dd-MMM-yyyy");
    const formattedTime = parseTime(legacyTime);
    return `${formattedDate} ${formattedTime}`;
  }
  if (legacyDate) {
    return format(parseISO(legacyDate), "dd-MMM-yyyy");
  }
  return "-";
};

/**
 * Gets formatted datetime with fallback to legacy date field.
 * Reduces boilerplate for the common UTC datetime + legacy fallback pattern.
 * @param {object} data - Data object containing datetime fields
 * @param {string} utcField - UTC datetime field name (e.g., 'actionDateTime')
 * @param {string} legacyField - Legacy date field name (e.g., 'actionDate')
 * @param {object} options - formatUtcToLocal options (variant, timeFormat)
 * @returns {string} Formatted datetime or fallback
 */
export const getDateTimeDisplay = (data, utcField, legacyField, options = {}) => {
  if (data?.[utcField]) {
    return formatUtcToLocal(data[utcField], options);
  }
  return changeDateFormat(data?.[legacyField], "DD-MMM-YYYY") || "-";
};

/**
 * Creates props for DateTimeDetailValue component with UTC support and legacy fallback.
 * Use this to maintain DateTimeDetailValue component structure while supporting UTC datetimes.
 * @param {object} data - Data object containing datetime fields
 * @param {string} utcField - UTC datetime field name (e.g., 'runEndDateTime')
 * @param {string} legacyDateField - Legacy date field name (e.g., 'runEndDate')
 * @param {string} legacyTimeField - Legacy time field name (e.g., 'runEndTime')
 * @returns {object} Props object for DateTimeDetailValue: { date, time, skipTimeParse }
 */
export const getDateTimeDetailProps = (data, utcField, legacyDateField, legacyTimeField) => {
  if (data?.[utcField]) {
    return {
      date: formatUtcToLocal(data[utcField], { variant: "date" }),
      time: formatUtcToLocal(data[utcField], { variant: "time" }),
      skipTimeParse: true
    };
  }
  return {
    date: changeDateFormat(data?.[legacyDateField], "DD-MMM-YYYY"),
    time: data?.[legacyTimeField]
  };
};

export const sortByPositionAscending = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.sort((a, b) => {
    const posA = Number(a.position_in_cluster ?? a._source?.position_in_cluster ?? 0);
    const posB = Number(b.position_in_cluster ?? b._source?.position_in_cluster ?? 0);

    return posA - posB;
  });
}
